/**
 * Notification Service — handles push + local notifications with dedup.
 *
 * Strategy:
 * - On app launch / login, fetch upcoming reminders and ALWAYS schedule local notifications
 *   (regardless of WS connectivity — this ensures offline-first delivery).
 * - When WS delivers reminder_fired, show an immediate notification AND cancel the
 *   matching scheduled local notification (dedup).
 * - If offline, the pre-scheduled local notification fires on its own at the right time.
 * - Heads-up: optionally schedule a "prepare" notification N minutes before the reminder.
 * - Notification IDs use `reminder-<seq>` so we can match and cancel precisely.
 */

import * as Notifications from 'expo-notifications';
import Constants from 'expo-constants';
import { Platform } from 'react-native';
import type { Reminder } from '../types/api';
import { requestWebPermission, showWebNotification } from './webNotifications';
import {
  NotificationResponseDedupe,
  notificationResponseKey,
} from './notificationResponseDedupe';

import AsyncStorage from '@react-native-async-storage/async-storage';

// Track recently-fired reminder seqs to prevent duplicate notifications.
// When the server sends reminder_fired over WS at the same instant the
// pre-scheduled local notification fires, both can trigger before the
// cancellation completes. This set ensures only one notification per seq
// within a 60-second window.
const _recentlyFired = new Map<string, number>(); // seq + occurrence → timestamp

// ─── Notification preferences (sound / vibration) ───
let _soundEnabled = true;
let _vibrationEnabled = true;

/** Load persisted notification preferences. Call once at startup. */
async function loadNotifPrefs(): Promise<void> {
  try {
    const [s, v] = await Promise.all([
      AsyncStorage.getItem('notif_sound'),
      AsyncStorage.getItem('notif_vibration'),
    ]);
    if (s !== null) _soundEnabled = s !== '0';
    if (v !== null) _vibrationEnabled = v !== '0';
  } catch {}
}

/** Update notification sound preference. */
export async function setNotifSound(enabled: boolean): Promise<void> {
  _soundEnabled = enabled;
  await AsyncStorage.setItem('notif_sound', enabled ? '1' : '0');
}

/** Update notification vibration preference. */
export async function setNotifVibration(enabled: boolean): Promise<void> {
  _vibrationEnabled = enabled;
  await AsyncStorage.setItem('notif_vibration', enabled ? '1' : '0');
}

/** Get current notification preference values. */
export function getNotifPrefs(): { sound: boolean; vibration: boolean } {
  return { sound: _soundEnabled, vibration: _vibrationEnabled };
}

// ─── Setup ───

/** Configure notification handler (must be called once at app startup) */
export function configureNotifications(): void {
  if (Platform.OS === 'web') return;
  loadNotifPrefs();
  Notifications.setNotificationHandler({
    handleNotification: async () => ({
      shouldShowAlert: true,
      shouldPlaySound: _soundEnabled,
      shouldSetBadge: true,
      shouldShowBanner: true,
      shouldShowList: true,
    }),
  });

  // Register notification action categories (Done / Snooze 15m)
  Notifications.setNotificationCategoryAsync('reminder', [
    { identifier: 'DONE', buttonTitle: '✅ Done', options: { opensAppToForeground: true } },
    { identifier: 'SNOOZE_15', buttonTitle: '⏰ Snooze 15m', options: { opensAppToForeground: true } },
  ]).catch(() => {});
}

/** Request notification permissions. Returns true if granted. */
export async function requestPermissions(): Promise<boolean> {
  if (Platform.OS === 'web') return requestWebPermission();
  const { status: existing } = await Notifications.getPermissionsAsync();
  if (existing === 'granted') return true;

  const { status } = await Notifications.requestPermissionsAsync();
  if (status !== 'granted') {
    console.warn('[Notif] Permission not granted');
    return false;
  }

  // Android channel + grouping
  if (Platform.OS === 'android') {
    await Notifications.setNotificationChannelGroupAsync('fishmemory', {
      name: 'FishMemory',
    });
    await Notifications.setNotificationChannelAsync('reminders', {
      name: 'Reminders',
      importance: Notifications.AndroidImportance.HIGH,
      sound: 'fishmemory_notif.wav',
      vibrationPattern: [0, 250, 250, 250],
      lockscreenVisibility: Notifications.AndroidNotificationVisibility.PUBLIC,
      groupId: 'fishmemory',
    });
  }

  return true;
}

/** Get the Expo push token for this device. Returns null if unavailable. */
export async function getPushToken(): Promise<string | null> {
  if (Platform.OS === 'web') return null;
  try {
    const projectId = Constants.expoConfig?.extra?.eas?.projectId;
    const { data } = await Notifications.getExpoPushTokenAsync({
      ...(projectId ? { projectId } : {}),
    });
    return data || null;
  } catch {
    return null;
  }
}

// ─── Immediate Notifications ───

/**
 * Show a notification immediately for a fired reminder.
 * Used when server delivers reminder_fired over WS.
 */
export async function showReminderNotification(seq: number, body: string, occurrenceRunAt?: string | null): Promise<void> {
  if (Platform.OS === 'web') {
    showWebNotification('⏰ Reminder', body.replace(/^#\d+\s*⏰\s*/, ''), { seq, type: 'reminder_fired', occurrenceRunAt });
    return;
  }
  // Dedup repeated delivery of this occurrence, without suppressing a distinct
  // minutely recurrence that happens to arrive less than 60 seconds later.
  const now = Date.now();
  const firedKey = `${seq}:${occurrenceRunAt || 'legacy'}`;
  const lastFired = _recentlyFired.get(firedKey);
  if (lastFired && now - lastFired < 60_000) return;
  _recentlyFired.set(firedKey, now);

  // Prune old entries
  for (const [s, t] of _recentlyFired) {
    if (now - t > 120_000) _recentlyFired.delete(s);
  }

  // Cancel any pre-scheduled local notification for this seq
  await cancelScheduledReminder(seq);

  await Notifications.scheduleNotificationAsync({
    identifier: `reminder-fired-${seq}`,
    content: {
      title: '⏰ Reminder',
      body: body.replace(/^#\d+\s*⏰\s*/, ''), // strip prefix if present
      data: { seq, type: 'reminder_fired', occurrenceRunAt },
      categoryIdentifier: 'reminder',
      sound: _soundEnabled ? 'fishmemory_notif.wav' : undefined,
      ...(Platform.OS === 'android' ? { channelId: 'reminders' } : {}),
    },
    trigger: null, // immediate
  });
}

// ─── Scheduled Local Notifications (offline fallback) ───

/**
 * Schedule a local notification for a future reminder.
 * If the WS delivers the reminder first, we cancel this.
 */
export async function scheduleReminderNotification(
  seq: number,
  body: string,
  fireAtUtc: string,
): Promise<void> {
  if (Platform.OS === 'web') return;
  const fireDate = new Date(fireAtUtc);
  const now = Date.now();
  const secondsUntil = Math.floor((fireDate.getTime() - now) / 1000);

  // Don't schedule if already past
  if (secondsUntil <= 0) return;

  // If less than 5 seconds away, fire immediately instead of dropping
  if (secondsUntil < 5) {
    await Notifications.scheduleNotificationAsync({
      identifier: `reminder-scheduled-${seq}`,
      content: {
        title: '⏰ Reminder',
        body,
        data: { seq, type: 'reminder_scheduled', occurrenceRunAt: fireAtUtc },
        categoryIdentifier: 'reminder',
        sound: _soundEnabled ? 'fishmemory_notif.wav' : undefined,
        ...(Platform.OS === 'android' ? { channelId: 'reminders' } : {}),
      },
      trigger: null, // immediate
    });
    return;
  }

  await Notifications.scheduleNotificationAsync({
    identifier: `reminder-scheduled-${seq}`,
    content: {
      title: '⏰ Reminder',
      body,
      data: { seq, type: 'reminder_scheduled', occurrenceRunAt: fireAtUtc },
      categoryIdentifier: 'reminder',
      sound: _soundEnabled ? 'fishmemory_notif.wav' : undefined,
      ...(Platform.OS === 'android' ? { channelId: 'reminders' } : {}),
    },
    trigger: { type: Notifications.SchedulableTriggerInputTypes.TIME_INTERVAL, seconds: secondsUntil },
  });
}

/** Cancel a scheduled local notification for a specific reminder seq */
export async function cancelScheduledReminder(seq: number): Promise<void> {
  if (Platform.OS === 'web') return;
  try {
    await Notifications.cancelScheduledNotificationAsync(`reminder-scheduled-${seq}`);
  } catch {
    // Notification may not exist — that's fine
  }
}

/** Cancel all scheduled reminder notifications */
export async function cancelAllScheduledReminders(): Promise<void> {
  if (Platform.OS === 'web') return;
  await Notifications.cancelAllScheduledNotificationsAsync();
}

// ─── Reschedule: rebuild all local notifications from local reminder data ───

/**
 * Reschedule local notifications from locally-persisted reminders.
 * ALWAYS schedules — no connectivity gate. Local notifications fire
 * on time even when offline; when WS delivers reminder_fired, the
 * matching scheduled notification is cancelled (dedup).
 *
 * Heads-up: if headsupMinutes > 0, also schedules a "prepare"
 * notification N minutes before each reminder.
 */
export async function rescheduleNotifications(
  reminders: Reminder[],
  opts?: { headsupEnabled?: boolean; headsupMinutes?: number },
): Promise<void> {
  if (Platform.OS === 'web') return;
  try {
    await cancelAllScheduledReminders();

    if (!reminders?.length) return;

    const now = Date.now();
    const horizon = now + 7 * 24 * 60 * 60 * 1000; // 7 days
    const headsupMs = (opts?.headsupEnabled !== false && (opts?.headsupMinutes ?? 5) > 0)
      ? (opts?.headsupMinutes ?? 5) * 60 * 1000
      : 0;

    for (const r of reminders) {
      // Skip paused, done (non-recurring with sent_at), or missing run_at
      if (r.is_paused) continue;
      if (r.sent_at_utc && !r.is_recurring) continue;
      if (!r.run_at_utc) continue;

      const fireTime = new Date(r.run_at_utc).getTime();
      if (fireTime > now && fireTime < horizon) {
        await scheduleReminderNotification(r.user_seq, r.body, r.run_at_utc);

        // Heads-up: schedule a "prepare" notification before the actual time
        if (headsupMs > 0) {
          const headsupTime = fireTime - headsupMs;
          if (headsupTime > now) {
            await scheduleHeadsupNotification(
              r.user_seq, r.body, headsupTime, opts?.headsupMinutes ?? 5,
            );
          }
        }
      }
    }
  } catch (err) {
    console.warn('[Notif] Failed to reschedule notifications:', err);
  }
}

/**
 * Schedule a "heads-up" notification N minutes before the actual reminder.
 */
async function scheduleHeadsupNotification(
  seq: number,
  body: string,
  fireAtMs: number,
  minutesBefore: number,
): Promise<void> {
  const secondsUntil = Math.floor((fireAtMs - Date.now()) / 1000);
  if (secondsUntil <= 0) return;

  await Notifications.scheduleNotificationAsync({
    identifier: `reminder-headsup-${seq}`,
    content: {
      title: `⏳ Coming up in ${minutesBefore} min`,
      body,
      data: { seq, type: 'reminder_headsup' },
      ...(Platform.OS === 'android' ? { channelId: 'reminders' } : {}),
    },
    trigger: { type: Notifications.SchedulableTriggerInputTypes.TIME_INTERVAL, seconds: secondsUntil },
  });
}

/** Cancel heads-up notification for a specific reminder seq */
export async function cancelHeadsupReminder(seq: number): Promise<void> {
  if (Platform.OS === 'web') return;
  try {
    await Notifications.cancelScheduledNotificationAsync(`reminder-headsup-${seq}`);
  } catch {
    // Notification may not exist — that's fine
  }
}

/** Update app icon badge count (overdue reminders). */
export async function updateBadgeCount(overdueCount: number): Promise<void> {
  if (Platform.OS === 'web') return;
  try {
    await Notifications.setBadgeCountAsync(Math.max(0, overdueCount));
  } catch {
    // Badge not supported or permission denied — ignore
  }
}

// ─── Notification Action Response Listener ───

/**
 * Register listener for notification quick-action responses (Done / Snooze).
 * Returns a subscription that should be removed on cleanup.
 */
export function addNotificationActionListener(
  onDone: (seq: number, occurrenceRunAt?: string | null) => void | Promise<void>,
  onSnooze: (seq: number, minutes: number, occurrenceRunAt?: string | null) => void | Promise<void>,
): Notifications.Subscription {
  const consumed = new NotificationResponseDedupe();
  const consume = (response: Notifications.NotificationResponse) => {
    const actionId = response.actionIdentifier;
    const data = response.notification.request.content.data as { seq?: number; occurrenceRunAt?: string | null } | undefined;
    const seq = data?.seq;
    if (!seq) return;
    if (actionId !== 'DONE' && actionId !== 'SNOOZE_15') return;

    const responseKey = notificationResponseKey({
      requestIdentifier: response.notification.request.identifier,
      actionIdentifier: actionId,
      occurrenceRunAt: data?.occurrenceRunAt,
      notificationDate: response.notification.date,
    });
    if (!consumed.claim(responseKey)) return;

    let action: void | Promise<void>;
    if (actionId === 'DONE') action = onDone(seq, data?.occurrenceRunAt);
    else action = onSnooze(seq, 15, data?.occurrenceRunAt);

    void Promise.resolve(action).catch(() => {}).finally(() => {
      try { Notifications.clearLastNotificationResponse(); } catch { /* unsupported platform */ }
    });
  };

  const subscription = Notifications.addNotificationResponseReceivedListener(consume);
  // A quick action can launch a previously killed app before React listeners
  // are registered. Consume the native-held response once initialization is ready.
  try {
    const initialResponse = Notifications.getLastNotificationResponse();
    if (initialResponse) consume(initialResponse);
  } catch { /* unsupported platform */ }

  return subscription;
}
